import { onTranslationsApplied, t } from './localization.ts';
import { markProgressStep, ProgressStep } from './progress.ts';
import { trackEvent } from './analytics.ts';

let btnPostComment: HTMLButtonElement | null = null;
let commentText: HTMLTextAreaElement | null = null;
let commentEmail: HTMLInputElement | null = null;
let commentStatus: HTMLElement | null = null;
let originalButtonText: string = '';

const COMMENT_PLACEHOLDER_KEY = 'slides.comments.prefill';
const COMMENT_PLACEHOLDER_FALLBACK =
  "YOU'RE AN ABSOLUTE ARSE! OF COURSE I ... ooh, that's how they get the engagement";

function updateCommentPlaceholder(): void {
  if (!commentText) return;
  const raw = t(COMMENT_PLACEHOLDER_KEY, COMMENT_PLACEHOLDER_FALLBACK);
  const value = typeof raw === 'string' ? raw.replace(/<br\s*\/?>(\s*)/gi, '\n') : raw;
  commentText.setAttribute('placeholder', value);
}

function isValidEmail(value: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
}

function updateCommentButtonState(): void {
  if (!btnPostComment) return;
  const hasContent = Boolean(commentText?.value?.trim());
  const emailValue = commentEmail?.value?.trim();
  const emailIsValid = !emailValue || isValidEmail(emailValue);
  btnPostComment.disabled = !(hasContent && emailIsValid);
}

function handleCommentInput(): void {
  if (commentStatus) commentStatus.textContent = '';
  updateCommentButtonState();
}

async function handleCommentSubmit(): Promise<void> {
  const text = commentText?.value?.trim();
  const email = commentEmail?.value?.trim();

  if (!text) {
    if (commentStatus) {
      commentStatus.textContent = t('comment.form.error', 'Please write something first!');
      commentStatus.style.color = '#000';
    }
    return;
  }

  if (email && !isValidEmail(email)) {
    if (commentStatus) {
      commentStatus.textContent = t(
        'comment.form.errorEmail',
        'Please enter a valid email address.'
      );
      commentStatus.style.color = '#000';
    }
    commentEmail?.focus();
    return;
  }

  // Disable button during submission
  if (btnPostComment) {
    btnPostComment.disabled = true;
    btnPostComment.classList.add('is-loading');
    btnPostComment.textContent = t('comment.form.sending', 'Sending...');
  }

  try {
    const response = await fetch('/api/comments', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ text, email }),
    });

    const result = await response.json();

    if (response.ok && result.success) {
      if (commentStatus) {
        commentStatus.textContent = t('comment.form.success', 'Comment sent!');
        commentStatus.style.color = '#000';
      }
      if (commentText) commentText.value = '';
      if (commentEmail) commentEmail.value = '';
      markProgressStep(ProgressStep.COMMENT_POSTED);
      trackEvent('comment_submit', {
        has_email: Boolean(email),
        comment_length: text.length,
      });
    } else {
      if (commentStatus) {
        commentStatus.textContent = result.error || 'Failed to send comment';
        commentStatus.style.color = '#d32f2f';
      }
    }
  } catch (error) {
    console.error('Comment submission failed:', error);
    if (commentStatus) {
      commentStatus.textContent = t('comment.form.networkError', 'Network error. Please try again.');
      commentStatus.style.color = '#d32f2f';
    }
  } finally {
    if (btnPostComment) {
      btnPostComment.classList.remove('is-loading');
      btnPostComment.textContent = originalButtonText;
    }
    updateCommentButtonState();
    window.setTimeout(() => {
      if (commentStatus) commentStatus.textContent = '';
    }, 5000);
  }
}

function initComments(): void {
  btnPostComment = document.getElementById('btnPostComment') as HTMLButtonElement | null;
  commentText = document.getElementById('commentText') as HTMLTextAreaElement | null;
  commentEmail = document.getElementById('commentEmail') as HTMLInputElement | null;
  commentStatus = document.getElementById('commentStatus');

  if (!btnPostComment || !commentText || !commentStatus) {
    window.requestAnimationFrame(initComments);
    return;
  }

  updateCommentPlaceholder();
  originalButtonText = btnPostComment.textContent;
  commentText.addEventListener('input', handleCommentInput);
  commentEmail?.addEventListener('input', handleCommentInput);
  btnPostComment.addEventListener('click', handleCommentSubmit);

  onTranslationsApplied(() => {
    updateCommentPlaceholder();
    updateCommentButtonState();
  });

  updateCommentButtonState();
}

export { initComments };
